import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n";
import { MessageCircle, Phone } from "lucide-react";
import { WHATSAPP_ENABLED } from "../lib/whatsapp";

/**
 * ContactGlobal — XEGA's "Hub Operacional".
 *
 * The most expressive section of the site. It tells the story of XEGA as a
 * regional operation with HQs across Latin America and lets the visitor reach
 * any of them in one tap.
 *
 * Visual stack:
 *   1. Animated "world arc" SVG with the four city pins, pulsing dots and
 *      light particles travelling between them through curved arcs.
 *   2. A row of glass / chrome city cards. Each card shows the country flag,
 *      city name, local time (auto-updating per timezone), a tel: button and
 *      a wa.me WhatsApp button.
 *
 * Phones are the official 800 numbers; WhatsApp numbers are still placeholders.
 */

export const OFFICES = [
  {
    id: "mx",
    city_es: "México",  city_en: "Mexico",     city_pt: "México",
    country_es: "México",         country_en: "Mexico",       country_pt: "México",
    flag: "🇲🇽",
    phone:    "800 700 1214",
    whatsapp: "+525512345678",
    tz: "America/Mexico_City",
    // Equirectangular: x=(lon+180)/360, y=(90-lat)/180
    coord: { lat: 19.4326, lon: -99.1332 },
    hue: 200, // cyan accent
  },
  {
    id: "co",
    city_es: "Colombia",            city_en: "Colombia",          city_pt: "Colômbia",
    country_es: "Colombia",       country_en: "Colombia",     country_pt: "Colômbia",
    flag: "🇨🇴",
    phone:    "800 057 20934",
    whatsapp: "+5712345678",
    tz: "America/Bogota",
    coord: { lat: 4.7110, lon: -74.0721 },
    hue: 50,
  },
  {
    id: "ar",
    city_es: "Argentina",      city_en: "Argentina",    city_pt: "Argentina",
    country_es: "Argentina",      country_en: "Argentina",    country_pt: "Argentina",
    flag: "🇦🇷",
    phone:    "800 054 30435",
    whatsapp: "+541112345678",
    tz: "America/Argentina/Buenos_Aires",
    coord: { lat: -34.6037, lon: -58.3816 },
    hue: 195,
  },
  {
    id: "br",
    city_es: "Brasil",    city_en: "Brazil",  city_pt: "Brasil",
    country_es: "Brasil",         country_en: "Brazil",       country_pt: "Brasil",
    flag: "🇧🇷",
    phone:    "800 055 10914",
    whatsapp: "+552112345678",
    tz: "America/Sao_Paulo",
    coord: { lat: -22.9068, lon: -43.1729 },
    hue: 140,
  },
];

/** Tight South-America-centric viewBox so the four pins fit nicely. */
const MAP = { vbW: 720, vbH: 380, lonMin: -170, lonMax: 175, latMin: -60, latMax: 75 };

function project({ lat, lon }) {
  const x = ((lon - MAP.lonMin) / (MAP.lonMax - MAP.lonMin)) * MAP.vbW;
  const y = ((MAP.latMax - lat) / (MAP.latMax - MAP.latMin)) * MAP.vbH;
  return { x, y };
}

function useLocalTime(tz) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz,
      }).format(now);
    } catch { return "--:--"; }
  }, [now, tz]);
}

export function OfficeCard({ office, idx }) {
  const { lang, pick } = useI18n();
  const time = useLocalTime(office.tz);
  const waDigits = office.whatsapp.replace(/\D/g, "");
  const cityLabel = pick(office.city_es, office.city_en, office.city_pt);
  const countryLabel = pick(office.country_es, office.country_en, office.country_pt);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: idx * 0.12 }}
      className="group relative"
      data-testid={`office-${office.id}`}
    >
      {/* Outer chrome bevel frame */}
      <div
        className="p-[3px] rounded-2xl transition-shadow duration-500 hover:shadow-[0_22px_36px_-14px_rgba(0,0,0,0.95),0_0_40px_-6px_rgba(0,240,255,0.65)]"
        style={{
          background:
            'linear-gradient(135deg, #cfd6df 0%, #f4f7fa 12%, #8a93a1 28%, #2b313b 50%, #8a93a1 72%, #f4f7fa 88%, #cfd6df 100%)',
        }}
      >
        <div className="relative rounded-[14px] overflow-hidden bg-gradient-to-br from-[#10141c] via-[#0b0f17] to-[#06080D] p-5 sm:p-6">
          {/* Animated ring tied to the country hue */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-50 group-hover:opacity-90 transition-opacity duration-500"
            style={{
              background: `radial-gradient(circle, hsla(${office.hue}, 95%, 60%, 0.45) 0%, transparent 65%)`,
              filter: 'blur(6px)',
            }}
          />

          {/* Flag + city + time row */}
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="text-3xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6"
                aria-hidden="true"
              >{office.flag}</span>
              <div className="min-w-0">
                <div className="font-display text-lg leading-tight truncate">{cityLabel}</div>
                <div className="label-mono !text-cyan-300/80 !tracking-[0.25em] text-[10px] truncate">{countryLabel.toUpperCase()}</div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono-x text-cyan-300 text-base leading-tight">{time}</div>
              <div className="label-mono !text-white/45 text-[9px]">{pick("HORA LOCAL", "LOCAL TIME", "HORA LOCAL")}</div>
            </div>
          </div>

          {/* Divider */}
          <div className="my-5 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

          {/* WhatsApp — primary CTA, green */}
          {WHATSAPP_ENABLED && <a
            href={`https://wa.me/${waDigits}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`office-${office.id}-whatsapp`}
            className="relative group/wa flex items-center justify-between gap-3 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-[#0d3f29] via-[#137a3b] to-[#10ff80]/30 border border-emerald-400/40 hover:border-emerald-300/80 transition overflow-hidden"
          >
            <span aria-hidden="true" className="absolute inset-0 opacity-70" style={{
              background: 'radial-gradient(circle at 80% 50%, rgba(16,255,128,0.35) 0%, transparent 60%)',
            }} />
            <span className="relative flex items-center gap-2.5">
              <MessageCircle className="w-4 h-4 text-emerald-300 group-hover/wa:scale-110 transition-transform" />
              <span className="font-tech text-sm text-white">WhatsApp</span>
            </span>
            <span className="relative font-mono-x text-[13px] text-emerald-100/90 group-hover/wa:text-white transition">
              {office.whatsapp}
            </span>
          </a>}

          {/* Phone — secondary */}
          <a
            href={`tel:${office.phone.replace(/\s/g, "")}`}
            data-testid={`office-${office.id}-phone`}
            className="mt-2 relative group/ph flex items-center justify-between gap-3 w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 hover:border-cyan-400/60 transition"
          >
            <span className="flex items-center gap-2.5">
              <Phone className="w-4 h-4 text-cyan-300" />
              <span className="font-tech text-sm text-white">{pick("Teléfono", "Phone", "Telefone")}</span>
            </span>
            <span className="font-mono-x text-[13px] text-white/80 group-hover/ph:text-cyan-200 transition">
              {office.phone}
            </span>
          </a>

          {/* Online indicator */}
          <div className="mt-4 flex items-center gap-2">
            <span className="pulse-dot" />
            <span className="label-mono !text-emerald-300 text-[10px]">{pick("EN LÍNEA · ATENCIÓN 24/7", "ONLINE · 24/7 SUPPORT", "ONLINE · ATENDIMENTO 24/7")}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function WorldArc() {
  const { pick } = useI18n();

  // Pixel-based coordinates measured against the Americas hub image
  // (1376 × 768 native). We normalise into a 1376×768 viewBox for the
  // SVG overlay so the paths line up exactly on the image.
  const IMG_W = 1376;
  const IMG_H = 768;
  const HUB = { x: 515, y: 315, label: "MÉXICO" };
  const NODES = [
    { id: "co", x: 745, y: 470, label: pick("BOGOTÁ", "BOGOTÁ", "BOGOTÁ") },
    { id: "br", x: 950, y: 595, label: pick("BRASIL", "BRAZIL", "BRASIL") },
    { id: "ar", x: 860, y: 685, label: pick("ARGENTINA", "ARGENTINA", "ARGENTINA") },
  ];

  // Build a nice curved path from the hub to a destination. The control
  // point is offset perpendicularly so each arc has personality.
  const arcPath = (dst) => {
    const mx = (HUB.x + dst.x) / 2;
    const my = (HUB.y + dst.y) / 2;
    const dx = dst.x - HUB.x;
    const dy = dst.y - HUB.y;
    // Perpendicular offset (curves the line upward/outward)
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const bend = Math.min(120, len * 0.28);
    const cx = mx + nx * bend;
    const cy = my + ny * bend - 30; // lift a bit
    return `M ${HUB.x} ${HUB.y} Q ${cx} ${cy} ${dst.x} ${dst.y}`;
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto" data-testid="world-arc">
      <style>{`
        @keyframes wa-pulse-ring { 0% { r: 8; opacity: 0.9; } 100% { r: 34; opacity: 0; } }
        @keyframes wa-particle  { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
        @keyframes wa-hub-glow  { 0%,100% { opacity: 0.55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }
        @keyframes wa-arc-fade  { 0%,100% { opacity: 0.55; } 50% { opacity: 0.95; } }
      `}</style>

      {/* Diffuse container — image blends naturally with the dark section */}
      <div className="relative">
        <div className="relative">
          {/* Base map image — Americas globe */}
          <img
            src="/hub-americas.jpg"
            alt="XEGA Americas Hub"
            className="w-full h-auto block select-none"
            draggable="false"
            style={{
              WebkitMaskImage:
                'radial-gradient(ellipse 90% 95% at center, rgba(0,0,0,1) 45%, rgba(0,0,0,0.75) 68%, rgba(0,0,0,0.25) 88%, rgba(0,0,0,0) 100%)',
              maskImage:
                'radial-gradient(ellipse 90% 95% at center, rgba(0,0,0,1) 45%, rgba(0,0,0,0.75) 68%, rgba(0,0,0,0.25) 88%, rgba(0,0,0,0) 100%)',
            }}
          />

          {/* SVG overlay for animated connections */}
          <svg
            viewBox={`0 0 ${IMG_W} ${IMG_H}`}
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              {/* Amber connection gradient */}
              <linearGradient id="wa2-arc" x1="0" x2="1">
                <stop offset="0%"  stopColor="#00E7FF" stopOpacity="0.0" />
                <stop offset="35%" stopColor="#FFB65A" stopOpacity="0.9" />
                <stop offset="65%" stopColor="#FFB65A" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#FF7A1A" stopOpacity="0.0" />
              </linearGradient>
              {/* Central hub glow */}
              <radialGradient id="wa2-hub" cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor="#9BE7FF" stopOpacity="1" />
                <stop offset="35%" stopColor="#00F0FF" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
              </radialGradient>
              <filter id="wa2-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" />
              </filter>
              <filter id="wa2-soft" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" />
              </filter>
            </defs>

            {/* Connection arcs — each destination curves from México (the Hub) */}
            {NODES.map((dst, i) => {
              const d = arcPath(dst);
              return (
                <g key={dst.id}>
                  {/* Wide amber underlay glow */}
                  <path d={d} fill="none" stroke="#FF9A3C" strokeWidth="5"
                        opacity="0.22" filter="url(#wa2-soft)" />
                  {/* Crisp gradient stroke */}
                  <path d={d} fill="none" stroke="url(#wa2-arc)" strokeWidth="1.6"
                        style={{ animation: `wa-arc-fade 4s ease-in-out ${i * 0.5}s infinite` }} />
                  {/* Travelling light particle — normalised path length so
                      the packet always finishes exactly at the destination. */}
                  <path d={d} fill="none" stroke="#FFE8B8" strokeWidth="2.5" strokeLinecap="round"
                        pathLength="1"
                        strokeDasharray="0.03 0.97"
                        style={{
                          animation: `wa-particle 4.2s linear ${i * 0.9}s infinite`,
                          filter: 'drop-shadow(0 0 6px rgba(255,190,90,0.95))',
                        }} />
                </g>
              );
            })}

            {/* Central hub — México */}
            <g>
              <circle cx={HUB.x} cy={HUB.y} r="48" fill="url(#wa2-hub)"
                      style={{ transformOrigin: `${HUB.x}px ${HUB.y}px`, animation: 'wa-hub-glow 3s ease-in-out infinite' }} />
              {/* Pulsing ring */}
              <circle cx={HUB.x} cy={HUB.y} r="8" fill="none"
                      stroke="#00F0FF" strokeWidth="2"
                      style={{ animation: 'wa-pulse-ring 2.4s ease-out infinite' }} />
              <circle cx={HUB.x} cy={HUB.y} r="10" fill="#00F0FF" filter="url(#wa2-glow)" />
              <circle cx={HUB.x} cy={HUB.y} r="5"  fill="#FFFFFF" />
              <text x={HUB.x} y={HUB.y - 26} textAnchor="middle"
                    fontFamily="'Orbitron','Rajdhani',sans-serif" fontWeight="800" fontSize="18" letterSpacing="4"
                    fill="#FFFFFF" opacity="0.98"
                    style={{ filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.9))' }}>
                {HUB.label}
              </text>
              <text x={HUB.x} y={HUB.y + 32} textAnchor="middle"
                    fontFamily="'Orbitron','Rajdhani',sans-serif" fontWeight="700" fontSize="12" letterSpacing="5"
                    fill="#9BE7FF" opacity="0.92"
                    style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.85))' }}>
                XEGA · HUB
              </text>
            </g>

            {/* Destination pins */}
            {NODES.map((n) => (
              <g key={`pin-${n.id}`} transform={`translate(${n.x} ${n.y})`}>
                <circle r="8" fill="none" stroke="#FF9A3C" strokeWidth="1.8"
                        style={{ animation: 'wa-pulse-ring 2.6s ease-out infinite' }} />
                <circle r="8" fill="none" stroke="#FF7A1A" strokeWidth="1.2"
                        style={{ animation: 'wa-pulse-ring 2.6s ease-out 1.2s infinite' }} />
                <circle r="6"  fill="#FF8A2A" filter="url(#wa2-glow)" />
                <circle r="3"  fill="#FFE0B0" />
                <text x="0" y="-16" textAnchor="middle"
                      fontFamily="'Orbitron','Rajdhani',sans-serif" fontWeight="700" fontSize="13" letterSpacing="2.5"
                      fill="#FFFFFF" opacity="0.98"
                      style={{ filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.95))' }}>
                  {n.label}
                </text>
              </g>
            ))}
          </svg>

          {/* Soft vignette to further blend with the section background */}
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: 'radial-gradient(circle at center, transparent 42%, rgba(6,8,13,0.65) 78%, rgba(6,8,13,0.95) 100%)' }} />

          {/* Top & bottom black fades for cleaner integration */}
          <div className="absolute inset-x-0 top-0 h-40 pointer-events-none"
               style={{ background: 'linear-gradient(to bottom, rgba(6,8,13,1) 0%, rgba(6,8,13,0.7) 35%, rgba(6,8,13,0) 100%)' }} />
          <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
               style={{ background: 'linear-gradient(to top, rgba(6,8,13,1) 0%, rgba(6,8,13,0.7) 35%, rgba(6,8,13,0) 100%)' }} />
        </div>
      </div>
    </div>
  );
}

export default function ContactGlobal() {
  const { pick } = useI18n();
  return (
    <section id="contact" className="relative pt-10 sm:pt-14 md:pt-20 pb-2 overflow-hidden" data-testid="contact-global-section">
      {/* Ambient backdrop — banner-matched warm amber + cyan atmosphere */}
      <div className="absolute top-0 -left-40 w-[480px] h-[480px] rounded-full bg-cyan-500/10 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -right-40 w-[520px] h-[520px] rounded-full bg-[#FF7A1A]/10 blur-[150px] pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        {/* Header hidden per client request (Feb 26, 2026) — kept in code to restore later
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 backdrop-blur mb-5">
            <span className="pulse-dot" />
            <span className="label-mono !text-cyan-300">{pick("HUB OPERACIONAL · LATAM", "OPERATIONS HUB · LATAM", "HUB OPERACIONAL · LATAM")}</span>
          </span>
          <h2 className="font-display text-[2rem] sm:text-4xl md:text-5xl chrome-text leading-tight">
            {pick("Estamos donde tú estás", "We are where you are", "Estamos onde você está")}
          </h2>
          <p className="mt-3 sm:mt-4 text-white/65 text-sm sm:text-base">
            {pick(
              "Cuatro sucursales conectadas en tiempo real. Atención local con respaldo regional.",
              "Four offices connected in real time. Local attention with regional backup.",
              "Quatro filiais conectadas em tempo real. Atendimento local com respaldo regional."
            )}
          </p>
        </motion.div>
        */}

        {/* Animated world arc */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="mb-12 sm:mb-14"
        >
          <WorldArc />
        </motion.div>

        {/* Office cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {OFFICES.map((o, i) => <OfficeCard key={o.id} office={o} idx={i} />)}
        </div>
      </div>
    </section>
  );
}
